import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../services/api';

const OnboardingContext = createContext(null);

export const useOnboarding = () => useContext(OnboardingContext);

/**
 * Onboarding steps — the 7-step Getting Started checklist.
 */
export const ONBOARDING_STEPS = [
  {
    id: 'create_project',
    label: 'Create a project',
    description: 'Set up your first engineering project to organize work',
    section: 'projects',
    icon: 'RocketLaunch',
    target: '[data-onboarding="create_project"]',
    hint: 'Click here to create your first project',
  },
  {
    id: 'upload_cad',
    label: 'Upload a CAD file',
    description: 'Import a drawing or 3D model to view and analyze',
    section: 'cad',
    icon: 'Architecture',
    target: '[data-onboarding="upload_cad"]',
    hint: 'Click Import to upload a CAD file (.dwg, .dxf, .step, etc.)',
    tutorial: 'cad_tutorial',
  },
  {
    id: 'ask_epiot',
    label: 'Ask e-pilot a question',
    description: 'Try the AI assistant — ask about your project or engineering topics',
    section: null,
    icon: 'Chat',
    target: '[data-onboarding="ask_epiot"]',
    hint: 'Type a question and press Enter to chat with e-pilot',
    tutorial: 'chat_tutorial',
  },
  {
    id: 'create_requirement',
    label: 'Create a requirement',
    description: 'Define an engineering requirement with acceptance criteria',
    section: 'requirements',
    icon: 'Checklist',
    target: '[data-onboarding="create_requirement"]',
    hint: 'Click here to add a new engineering requirement',
    tutorial: 'requirements_tutorial',
  },
  {
    id: 'upload_document',
    label: 'Upload a document',
    description: 'Add a spec, report, or reference document to your project',
    section: 'documents',
    icon: 'Description',
    target: '[data-onboarding="upload_document"]',
    hint: 'Click here to upload a document to your project',
    tutorial: 'documents_tutorial',
  },
  {
    id: 'run_compliance',
    label: 'Run a compliance check',
    description: 'Check a CAD drawing against requirements or standards',
    section: 'compliance',
    icon: 'VerifiedUser',
    target: '[data-onboarding="run_compliance"]',
    hint: 'Open a CAD file and run a compliance check against your standards',
    tutorial: 'compliance_tutorial',
  },
  {
    id: 'explore_kb',
    label: 'Explore the Knowledge Base',
    description: 'See how pinned documents power e-pilot\'s AI context',
    section: 'knowledge-base',
    icon: 'LibraryBooks',
    target: '[data-onboarding="explore_kb"]',
    hint: 'Browse the Knowledge Base to see what e-pilot can access',
    tutorial: 'knowledge_base_tutorial',
  },
  {
    id: 'try_orchestrator',
    label: 'Build a pipeline',
    description: 'Connect engineering tools in the visual orchestrator',
    section: 'orchestrator',
    icon: 'AccountTree',
    target: '[data-onboarding="try_orchestrator"]',
    hint: 'Drag tools onto the canvas and click Generate Plan',
    tutorial: 'orchestrator_tutorial',
  },
];

/**
 * Project Tutorial Phases — interactive walkthrough after project creation.
 *
 * Two types:
 *   'action'   — spotlight a button, wait for user to do it
 *   'educate'  — show a dialog with slides (uses slideGroup to pick content)
 */
export const TUTORIAL_PHASES = [
  // Teach about the Home tab (project auto-opened after creation)
  { id: 'learn_home',      type: 'educate', slideGroup: 'home' },
  // User creates a branch (auto-switches to Versioning sub-tab)
  { id: 'create_branch',   type: 'action',  target: '[data-onboarding="create-branch"]',    hint: 'Click "New Branch" to create your first branch',  event: 'tutorial:branch-created', prepare: 'tutorial:show-versioning' },
  // Teach about branching, merging, conflicts, and the full workflow
  // (merging is taught conceptually with diagrams — an empty branch has nothing to merge)
  { id: 'learn_branches',  type: 'educate', slideGroup: 'branches' },
  // Teach about commits, tags, releases, milestones
  { id: 'learn_vc',        type: 'educate', slideGroup: 'version_control' },
  // User opens Analytics from sidebar
  { id: 'open_analytics',  type: 'action',  target: '[data-tour="global-nav"]',    hint: 'Click Analytics in the sidebar to see project insights',  event: 'tutorial:analytics-opened' },
  // Teach about analytics
  { id: 'learn_analytics', type: 'educate', slideGroup: 'analytics' },
];

export const OnboardingProvider = ({ children }) => {
  const { user } = useAuth();
  const [progress, setProgress] = useState({});
  const [dismissed, setDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [activeGuide, setActiveGuide] = useState(null);
  // Project tutorial phase tracking (for the create_project interactive walkthrough)
  const [tutorialActive, setTutorialActive] = useState(false);
  const [tutorialPhaseIndex, setTutorialPhaseIndex] = useState(0);
  const [showTutorialDialog, setShowTutorialDialog] = useState(false);
  // Section tutorial (simple dialog shown after completing any non-project step)
  const [sectionTutorial, setSectionTutorial] = useState(null); // slideGroup key or null
  const saveTimerRef = useRef(null);
  const progressRef = useRef(progress);
  progressRef.current = progress;

  // Load onboarding progress from the user profile
  useEffect(() => {
    if (!user) return;
    api.getUserProfile().then(res => {
      const profile = res.data;
      setProgress(profile.onboarding_progress || {});
      setDismissed(!!profile.onboarding_completed || !!profile.onboarding_progress?.dismissed);
      setLoaded(true);
    }).catch(() => {
      setLoaded(true);
    });
  }, [user]);

  // Debounced save to backend (500ms)
  const saveToBackend = useCallback((newProgress) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      api.updateOnboardingProgress(newProgress).catch(() => {});
    }, 500);
  }, []);

  const completeStep = useCallback((stepId) => {
    if (dismissed) return; // User dismissed onboarding — don't show anything
    setProgress(prev => {
      if (prev[stepId]) return prev; // Already completed — skip
      const updated = { ...prev, [stepId]: Date.now() };
      saveToBackend(updated);
      return updated;
    });
    setActiveGuide(prev => prev === stepId ? null : prev);
  }, [saveToBackend, dismissed]);

  const isStepComplete = useCallback((stepId) => {
    return !!progress[stepId];
  }, [progress]);

  const startGuide = useCallback((stepId) => {
    setActiveGuide(stepId);
  }, []);

  const resetStep = useCallback((stepId) => {
    setProgress(prev => {
      const updated = { ...prev };
      delete updated[stepId];
      saveToBackend(updated);
      return updated;
    });
  }, [saveToBackend]);

  const replayStep = useCallback((stepId) => {
    resetStep(stepId);
    if (stepId === 'create_project') {
      setTimeout(() => {
        setTutorialActive(true);
        setTutorialPhaseIndex(0);
      }, 300);
      return;
    }
    setActiveGuide(stepId);
  }, [resetStep]);

  const stopGuide = useCallback(() => {
    setActiveGuide(null);
  }, []);

  // --- Tutorial phase management ---
  const currentTutorialPhase = tutorialActive ? TUTORIAL_PHASES[tutorialPhaseIndex] : null;

  const advanceTutorial = useCallback(() => {
    const nextIndex = tutorialPhaseIndex + 1;
    if (nextIndex >= TUTORIAL_PHASES.length) {
      setTutorialActive(false);
      setTutorialPhaseIndex(0);
      setShowTutorialDialog(false);
      return;
    }
    setTutorialPhaseIndex(nextIndex);
    const nextPhase = TUTORIAL_PHASES[nextIndex];
    if (nextPhase.type === 'educate') {
      setShowTutorialDialog(true);
    } else {
      setShowTutorialDialog(false);
      // Dispatch prepare event so the target component can set up
      // (e.g. switch to the correct sub-tab before the spotlight appears)
      if (nextPhase.prepare) {
        window.dispatchEvent(new CustomEvent(nextPhase.prepare));
      }
    }
  }, [tutorialPhaseIndex]);

  const skipTutorial = useCallback(() => {
    setTutorialActive(false);
    setTutorialPhaseIndex(0);
    setShowTutorialDialog(false);
  }, []);

  // When a phase is 'educate', auto-show the dialog
  useEffect(() => {
    if (currentTutorialPhase?.type === 'educate') {
      setShowTutorialDialog(true);
    }
  }, [currentTutorialPhase]);

  // Listen for tutorial phase events (action completion)
  useEffect(() => {
    if (!tutorialActive || !currentTutorialPhase || currentTutorialPhase.type !== 'action') return;

    const expectedEvent = currentTutorialPhase.event;
    const handler = () => {
      // Small delay for UX so user sees the result of their action
      setTimeout(() => advanceTutorial(), 600);
    };

    window.addEventListener(expectedEvent, handler);
    return () => window.removeEventListener(expectedEvent, handler);
  }, [tutorialActive, currentTutorialPhase, advanceTutorial]);

  // Listen for onboarding step completion events
  useEffect(() => {
    const handler = (e) => completeStep(e.detail);
    window.addEventListener('onboarding:complete', handler);
    return () => window.removeEventListener('onboarding:complete', handler);
  }, [completeStep]);

  const completedCount = ONBOARDING_STEPS.filter(s => progress[s.id]).length;
  const totalSteps = ONBOARDING_STEPS.length;
  const allComplete = completedCount === totalSteps;
  const nextStep = ONBOARDING_STEPS.find(s => !progress[s.id]) || null;

  const dismiss = useCallback(() => {
    setDismissed(true);
    setActiveGuide(null);
    saveToBackend({ ...progress, dismissed: true });
    api.completeOnboarding().catch(() => {}); // persist so it survives hard refresh
  }, [progress, saveToBackend]);

  const reopen = useCallback(() => {
    setDismissed(false);
    const { dismissed: _, ...rest } = progress;
    setProgress(rest);
    saveToBackend(rest);
  }, [progress, saveToBackend]);

  const skipAll = useCallback(() => {
    setDismissed(true);
    setActiveGuide(null);
    setTutorialActive(false);
    const skipped = { ...progress, dismissed: true, skipped: true };
    setProgress(skipped);
    saveToBackend(skipped);
    api.completeOnboarding().catch(() => {});
  }, [progress, saveToBackend]);

  // Reset all progress and reopen checklist from scratch
  const restartTutorial = useCallback(() => {
    setProgress({});
    setDismissed(false);
    setActiveGuide(null);
    setTutorialActive(false);
    setTutorialPhaseIndex(0);
    setShowTutorialDialog(false);
    setSectionTutorial(null);
    localStorage.removeItem('eis_welcome_seen');
    api.updateOnboardingProgress({ _reset: true }).catch(() => {});
  }, []);

  return (
    <OnboardingContext.Provider value={{
      progress, completedCount, totalSteps, allComplete, loaded, dismissed,
      activeGuide, nextStep,
      completeStep, isStepComplete, startGuide, stopGuide,
      dismiss, reopen, skipAll, resetStep, replayStep, restartTutorial,
      // Tutorial
      tutorialActive, currentTutorialPhase, tutorialPhaseIndex,
      showTutorialDialog, setShowTutorialDialog,
      advanceTutorial, skipTutorial,
      // Section tutorials
      sectionTutorial, setSectionTutorial,
      steps: ONBOARDING_STEPS,
    }}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext;
